import { useEffect, useRef, useState } from 'react';
import { useSocket } from 'socket.io-react-hook';
import { _getToken } from 'src/Config/axiosApi';
import { UnwindTrace, UnwindLog } from '../types/unwind-debug.types';

const FLUSH_INTERVAL_MS = 100;
const MAX_RING = 500;

export interface DebugLiveState {
  isConnected: boolean;
  traces: UnwindTrace[];
  logs: UnwindLog[];
}

/**
 * Live tail for the Debug tab. Connects to /unwind-debug, optionally filtered
 * by pair, and buffers incoming events; flushes to React state at most every
 * 100ms to avoid render thrash.
 *
 * Holds a bounded ring buffer of the most recent MAX_RING events per kind
 * — older events are evicted as new ones arrive.
 */
export function useUnwindDebugSocket(opts: {
  pairId?: string;
  enabled?: boolean;
}): DebugLiveState {
  const wsUrl = import.meta.env.VITE_POSITION_UNWINDER_URL;
  const accessToken = _getToken();
  const enabled = opts.enabled !== false;

  const { socket } = useSocket((wsUrl || '') + '/unwind-debug', {
    enabled: enabled && !!wsUrl && !!accessToken,
    transports: ['websocket'],
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  const [state, setState] = useState<DebugLiveState>({
    isConnected: false,
    traces: [],
    logs: [],
  });

  const traceBuf = useRef<UnwindTrace[]>([]);
  const logBuf = useRef<UnwindLog[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setState((s) => ({ ...s, isConnected: socket.connected }));
  }, [socket.connected]);

  // (re)subscribe when filter changes
  useEffect(() => {
    if (!socket.connected) return;
    socket.emit('subscribe', opts.pairId ? { pairId: opts.pairId } : {});
  }, [socket, socket.connected, opts.pairId]);

  useEffect(() => {
    if (!enabled) return;

    const onTrace = (evt: UnwindTrace) => {
      traceBuf.current.push(evt);
      scheduleFlush();
    };
    const onLog = (evt: UnwindLog) => {
      logBuf.current.push(evt);
      scheduleFlush();
    };
    const scheduleFlush = () => {
      if (flushTimer.current) return;
      flushTimer.current = setTimeout(() => {
        flushTimer.current = null;
        const newTraces = traceBuf.current;
        const newLogs = logBuf.current;
        traceBuf.current = [];
        logBuf.current = [];
        if (newTraces.length === 0 && newLogs.length === 0) return;
        setState((s) => ({
          isConnected: socket.connected,
          traces: trimRing([...newTraces.reverse(), ...s.traces]),
          logs: trimRing([...newLogs.reverse(), ...s.logs]),
        }));
      }, FLUSH_INTERVAL_MS);
    };

    socket.on('trace', onTrace);
    socket.on('log', onLog);
    return () => {
      socket.off('trace', onTrace);
      socket.off('log', onLog);
      if (flushTimer.current) {
        clearTimeout(flushTimer.current);
        flushTimer.current = null;
      }
    };
  }, [socket, enabled]);

  return state;
}

function trimRing<T>(arr: T[]): T[] {
  return arr.length > MAX_RING ? arr.slice(0, MAX_RING) : arr;
}
